import React, { useEffect, useState } from 'react';
import styled from 'styled-components/macro';
import { UserContext, UserEmpty, UserT } from '@stores/UserContext';

import RoutesProtected from '@app_config/RoutesProtected';
import RoutesPublic from '@app_config/RoutesPublic';
import SideMenu from './components/menus/SideMenu';
import LoadingMessage from '@as_core/account/LoadingMessage';

import { CreditsContext } from '@stores/CreditsContext';
import { MenuContext } from '@stores/MenuContext';
import useCreditsInfo, { CreditsT } from './utils/useCreditsInfo';
import AuthWrapper from '@as_core/account/AuthWrapper';
import useUserInfo from '@utils/useUserInfo';
import FailedLoadingUserAppData from '@as_core/account/FailedLoadingUserAppData';

const debug: boolean = false;

const App = () => {
  const { getUserInfo } = useUserInfo();
  const { getCreditsInfo } = useCreditsInfo();
  const [isInitialLaunch, setIsInitialLaunch] = useState<boolean>(true);
  const [isAppDataLoading, setIsAppDataLoading] = useState<boolean>(false);
  const [failedLoadingUserData, setFailedLoadingUserData] = useState<boolean>(false);
  const [user, setUser] = useState<UserT | null>(UserEmpty);
  // can't we just store the credits as part of the user store ?
  const [creditsInfo, setCreditsInfo] = useState<CreditsT | null>({
    creditsUsed: 0,
    creditsAvailable: 0,
  });
  debug && console.log('App | process.env', process.env);

  // use for side menu
  const [activeMenuId, setActiveMenuId] = useState<string>(null);
  const menuParams = React.useMemo(
    () => ({ activeMenuId, setActiveMenuId }),
    [activeMenuId, setActiveMenuId]
  );

  const myCreditsInfo = React.useMemo(
    () => ({ creditsInfo, setCreditsInfo }),
    [creditsInfo, setCreditsInfo]
  );

  // Get the User Application Account Information -- after authenticated and registration loaded
  useEffect(() => {
    if (user.isRegistered && !user.isAppAuthorized && !isAppDataLoading && !failedLoadingUserData) {
      setIsAppDataLoading(true);
      if (isInitialLaunch) setIsInitialLaunch(false);
      let updated_creditsInfo = creditsInfo;
      let token = user.authSession.getAccessToken().getJwtToken();
      getUserInfo(token, {login: true})
        .then((response) => {
          if (debug) console.log('App | getUserInfo | response:', response);
          if (response === null) {  // error in creating account
            setUser((prev) => ({ ...prev, isAppAuthorized: false }));
            setIsAppDataLoading(false);
            setFailedLoadingUserData(true);
          } else {
            getCreditsInfo(token).then((response) => {
              // @ts-ignore
              debug && console.log('App | Credits response: ', response);
              if (response?.data) {
                updated_creditsInfo = response?.data;
                debug &&
                console.log(
                  'App | Credits updated_creditsInfo: ',
                  updated_creditsInfo
                );
              }
              setCreditsInfo(updated_creditsInfo);
              setUser((prev) => ({ ...prev, isAppAuthorized: true }));
              setIsAppDataLoading(false);
            })
          }
        })
        .catch(()=> {
          console.error('Error on loading user App data');
          setUser((prev) => ({ ...prev, isAppAuthorized: false }));
          setIsAppDataLoading(false);
          setFailedLoadingUserData(true);
        });
    }
  }, [user, isAppDataLoading]);

  debug && console.log('App | {user}', user);
  // when all API calls for user authentication/authorization are done
  return (
    <UserContext.Provider value={{ user, setUser }}>
      <AuthWrapper>
        <MenuContext.Provider value={menuParams}>
          <CreditsContext.Provider value={myCreditsInfo}>
            <Container>
              {isAppDataLoading ? (
                <LoadingMessage message={'Loading User Application Data'} />
              ) : user.isLoading && !isInitialLaunch ? (
                <LoadingMessage message={'Checking Authentication'} />
              ) : user.isAuthenticated && user.isRegistered && !failedLoadingUserData ? (
                <>
                  <SideMenu user={user} />
                  <RoutesProtected />
                </>
              ) : failedLoadingUserData ? (
                <FailedLoadingUserAppData
                  reset={() => setFailedLoadingUserData(false)}
                />
              ) : (
                <RoutesPublic />
              )}
            </Container>
          </CreditsContext.Provider>
        </MenuContext.Provider>
      </AuthWrapper>
    </UserContext.Provider>
  );
};
export default App;

const Container = styled.div`
  position: fixed;
  top: 0;
  bottom: 0;
  right: 0;
  left: 0;
  display: grid;
  grid-template-columns: 1fr;
  justify-content: center;
  align-content: start;
  background: ${(p) => p.theme.palette.backgroundPrimary};
`;
